import { CircleCheckIcon, CircleDashedIcon, CircleDotDashedIcon, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DbAgentStage } from "@/types/db";

// Lifecycle stage colours and pill (docs/agent-lifecycle.md). Same pill shape
// as the request-status pills (src/app/widgets/requests/page.tsx).
//
// Stage colours use the design skill's one sanctioned palette exception —
// light status tints (bg-*-500/15 text-*-600), as the roadmap pills do — so
// no new theme tokens are needed and both themes read them the same way.
// Use AGENT_STAGE_STYLES wherever a stage needs colour, never ad-hoc classes.

export const AGENT_STAGE_LABELS: Record<DbAgentStage, string> = {
  draft: "Draft",
  provisional: "Provisional",
  reviewed: "Reviewed",
};

export const AGENT_STAGE_STYLES: Record<
  DbAgentStage,
  {
    /** Chips/badges: light tint + coloured text. */
    pill: string;
    /** Flat marker (light tint + coloured icon), e.g. a completed tracker step. */
    solid: string;
    /** Flat marker with a coloured ring, e.g. the current tracker step. */
    outline: string;
    /** Coloured text, e.g. the current step's label. */
    text: string;
    /** Connector/progress line. */
    line: string;
  }
> = {
  draft: {
    pill: "bg-amber-500/15 text-amber-600",
    solid: "border-transparent bg-amber-500/15 text-amber-600",
    outline: "border-amber-500/60 bg-amber-500/15 text-amber-600 ring-2 ring-amber-500/20",
    text: "text-amber-600",
    line: "bg-amber-500/50",
  },
  provisional: {
    pill: "bg-sky-500/15 text-sky-600",
    solid: "border-transparent bg-sky-500/15 text-sky-600",
    outline: "border-sky-500/60 bg-sky-500/15 text-sky-600 ring-2 ring-sky-500/20",
    text: "text-sky-600",
    line: "bg-sky-500/50",
  },
  reviewed: {
    pill: "bg-green-500/15 text-green-600",
    solid: "border-transparent bg-green-500/15 text-green-600",
    outline: "border-green-500/60 bg-green-500/15 text-green-600 ring-2 ring-green-500/20",
    text: "text-green-600",
    line: "bg-green-500/50",
  },
};

/**
 * One circle family, filling in as an agent progresses: dashed (Draft) →
 * dashed with a dot (Provisional) → checked (Reviewed). Static lucide icons:
 * lucide-animated has none of these.
 */
export const AGENT_STAGE_ICONS: Record<DbAgentStage, LucideIcon> = {
  draft: CircleDashedIcon,
  provisional: CircleDotDashedIcon,
  reviewed: CircleCheckIcon,
};

export function AgentStageIcon({ stage, className }: { stage: DbAgentStage; className?: string }) {
  const Icon = AGENT_STAGE_ICONS[stage];
  return <Icon aria-hidden className={cn("size-4 shrink-0", AGENT_STAGE_STYLES[stage].text, className)} />;
}

const PILL = "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium";

export function AgentStageBadge({ stage, className }: { stage: DbAgentStage; className?: string }) {
  return (
    <span className={cn(PILL, "gap-1", AGENT_STAGE_STYLES[stage].pill, className)}>
      <AgentStageIcon stage={stage} className="size-3 text-current" />
      {AGENT_STAGE_LABELS[stage]}
    </span>
  );
}

export function AgentApprovedBadge({ className }: { className?: string }) {
  return <span className={cn(PILL, "bg-activated/15 text-activated", className)}>Approved</span>;
}

/** Score vs cut-off, green when it passes. */
export function AgentScoreBadge({
  score,
  threshold,
  passed: runPassed,
  className,
}: {
  score: number | null;
  threshold: number | null;
  passed?: boolean | null;
  className?: string;
}) {
  if (score === null || score === undefined) {
    return <span className={cn(PILL, "bg-muted text-muted-foreground", className)}>Not tested</span>;
  }
  const passed = runPassed ?? (threshold == null ? null : Number(score) >= threshold);
  return (
    <span
      className={cn(
        PILL,
        "tabular-nums",
        passed === null ? "bg-muted text-muted-foreground" : passed ? "bg-activated/15 text-activated" : "bg-destructive/15 text-destructive",
        className,
      )}
    >
      {Number(score).toFixed(0)}%
    </span>
  );
}
